import React from 'react';
import type { AccountTransaction } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { TransactionInvoiceCard } from '../../Transaction/Card/TransactionInvoiceCard';

export interface AccountTransactionInvoicesTabProps {
  transaction: AccountTransaction;
}

/**
 * Per-invoice deltas embedded on the bilateral transaction.
 *
 * `AccountTransaction.invoices[]` reuses {@link TransactionInvoice}
 * verbatim — same per-invoice shape (`ref`, `amount`,
 * `paidAmountChange`, `invoiceCurrentTotal`). That lets us re-use
 * the existing `TransactionInvoiceCard` directly instead of
 * re-implementing the card for the bilateral feature.
 */
export function AccountTransactionInvoicesTab({
  transaction,
}: AccountTransactionInvoicesTabProps) {
  const { t } = useTranslation(['m-account-transaction', 'm-payment']);

  const invoices = transaction.invoices ?? [];

  if (invoices.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        {t('m-account-transaction:drawer.invoices.no_invoices', {
          defaultValue: t('m-payment:transaction.drawer.invoices.no_invoices'),
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {invoices.map((inv, index) => (
        <TransactionInvoiceCard
          key={inv._id?.toString() || index}
          invoice={inv}
          currency={transaction.currency}
        />
      ))}
    </div>
  );
}
